import * as SelectPrimitive from "@radix-ui/react-select";
import { IconCheck, IconChevronDown, IconSearch } from "@tabler/icons-react";
import { forwardRef, useMemo, useState, type ReactNode } from "react";
import { cn } from "@/lib/cn";
import { TEXT_SIZE_CLASS, type Scale } from "./tokens";

const HEIGHT_CLASS = {
  xs: "h-7",
  sm: "h-8",
  md: "h-9",
  lg: "h-10",
  xl: "h-12",
} as const;

export interface SelectOption {
  value: string;
  label: ReactNode;
  disabled?: boolean;
}

export interface SelectProps {
  options: SelectOption[];
  value?: string;
  defaultValue?: string;
  onValueChange?: (value: string) => void;
  placeholder?: string;
  label?: ReactNode;
  description?: ReactNode;
  error?: ReactNode;
  size?: Scale;
  disabled?: boolean;
  required?: boolean;
  className?: string;
  wrapperClassName?: string;
  name?: string;
  id?: string;
  /** Agrega un input de filtro arriba de la lista · para catálogos largos (CeCo, cuentas, claves SAT, proveedores). */
  searchable?: boolean;
  /** Placeholder del input de búsqueda cuando `searchable`. */
  searchPlaceholder?: string;
  /** Clases extra del panel desplegable (p. ej. un ancho mínimo mayor que el del trigger). */
  contentClassName?: string;
}

/**
 * Reemplazo de `<Select>` de Mantine (Radix UI) · misma altura/borde que
 * `Input` para que quepan lado a lado en un formulario. Con `searchable` se
 * agrega un `<input>` de texto plano arriba del `Viewport` que filtra
 * `options` en memoria (sin `cmdk` · Radix Select no da typeahead libre, así
 * que el filtro se hace a mano y se detiene la propagación de teclas para
 * que Radix no le gane el foco al input).
 */
export const Select = forwardRef<HTMLButtonElement, SelectProps>(
  (
    {
      options,
      value,
      defaultValue,
      onValueChange,
      placeholder = "Selecciona…",
      label,
      description,
      error,
      size = "sm",
      disabled,
      required,
      className,
      wrapperClassName,
      name,
      id,
      searchable = false,
      searchPlaceholder = "Buscar…",
      contentClassName,
    },
    ref
  ) => {
    const autoId = id ?? name;
    const [filtro, setFiltro] = useState("");
    const tituloSeleccion = useMemo(() => {
      const actual = options.find((o) => o.value === (value ?? defaultValue));
      return typeof actual?.label === "string" ? actual.label : undefined;
    }, [options, value, defaultValue]);
    const opcionesFiltradas = useMemo(() => {
      if (!searchable || !filtro.trim()) return options;
      const q = filtro.trim().toLowerCase();
      return options.filter((o) => String(o.label).toLowerCase().includes(q) || o.value.toLowerCase().includes(q));
    }, [options, filtro, searchable]);
    return (
      <div className={cn("flex flex-col gap-1", wrapperClassName)}>
        {label && (
          <label htmlFor={autoId} className="text-sm font-medium text-foreground">
            {label}
            {required && <span className="text-peligro-600"> *</span>}
          </label>
        )}
        {description && <p className="text-xs text-muted-foreground">{description}</p>}
        <SelectPrimitive.Root
          value={value}
          defaultValue={defaultValue}
          onValueChange={onValueChange}
          disabled={disabled}
          name={name}
          onOpenChange={(open) => {
            if (!open) setFiltro("");
          }}
        >
          <SelectPrimitive.Trigger
            ref={ref}
            id={autoId}
            className={cn(
              "flex w-full items-center justify-between gap-2 rounded-md border bg-background px-3 transition-[border-color,box-shadow] ease-out-quint duration-140",
              "focus:outline-none focus:ring-2 focus:ring-multiva-600/30 focus:border-multiva-600",
              "disabled:opacity-50 disabled:cursor-not-allowed",
              "data-[placeholder]:text-muted-foreground",
              error ? "border-peligro-600" : "border-input",
              HEIGHT_CLASS[size],
              TEXT_SIZE_CLASS[size],
              className
            )}
          >
            {/* Una sola línea con «…» (las etiquetas largas · p. ej. CeCo/cuenta en el
                prorrateo · se partían en varias líneas y se salían del control); la etiqueta
                completa queda en el tooltip nativo (`title`). */}
            <span className="min-w-0 flex-1 truncate text-left" title={tituloSeleccion}>
              <SelectPrimitive.Value placeholder={placeholder} />
            </span>
            <SelectPrimitive.Icon className="shrink-0">
              <IconChevronDown size={14} className="text-muted-foreground" />
            </SelectPrimitive.Icon>
          </SelectPrimitive.Trigger>
          <SelectPrimitive.Portal>
            <SelectPrimitive.Content
              position="popper"
              sideOffset={4}
              className={cn(
                "z-[1050] max-h-72 w-[var(--radix-select-trigger-width)] overflow-y-auto rounded-md border border-border bg-popover text-popover-foreground shadow-lg",
                "data-[state=open]:animate-fade-in data-[state=closed]:animate-fade-out",
                contentClassName
              )}
            >
              {searchable && (
                <div className="flex items-center gap-1.5 border-b border-border px-2 py-1.5">
                  <IconSearch size={14} className="shrink-0 text-muted-foreground" />
                  <input
                    autoFocus
                    value={filtro}
                    onChange={(e) => setFiltro(e.currentTarget.value)}
                    onKeyDown={(e) => e.stopPropagation()}
                    placeholder={searchPlaceholder}
                    className="w-full bg-transparent text-sm outline-none placeholder:text-muted-foreground"
                  />
                </div>
              )}
              <SelectPrimitive.Viewport className="p-1">
                {opcionesFiltradas.length === 0 ? (
                  <p className="px-2 py-3 text-center text-xs text-muted-foreground">Sin resultados.</p>
                ) : (
                  opcionesFiltradas.map((opt) => (
                    <SelectPrimitive.Item
                      key={opt.value}
                      value={opt.value}
                      disabled={opt.disabled}
                      className={cn(
                        "relative flex cursor-pointer items-center rounded-sm py-1.5 pl-7 pr-2 text-sm outline-none",
                        "focus:bg-muted data-[disabled]:pointer-events-none data-[disabled]:opacity-50",
                        TEXT_SIZE_CLASS[size]
                      )}
                    >
                      <SelectPrimitive.ItemIndicator className="absolute left-2 flex items-center">
                        <IconCheck size={14} />
                      </SelectPrimitive.ItemIndicator>
                      <SelectPrimitive.ItemText>{opt.label}</SelectPrimitive.ItemText>
                    </SelectPrimitive.Item>
                  ))
                )}
              </SelectPrimitive.Viewport>
            </SelectPrimitive.Content>
          </SelectPrimitive.Portal>
        </SelectPrimitive.Root>
        {error && typeof error !== "boolean" && <p className="text-xs text-peligro-600">{error}</p>}
      </div>
    );
  }
);
Select.displayName = "Select";
